import type { ToolLifecycle } from '../tools/tool-lifecycle'
import type { StreamEvent } from './llm'

import { duplicateToolResult, getToolLifecycleByName, ignoredAfterTerminalResult, normalizeToolResult, ToolExecutionLedger, ToolInputError } from '../tools/tool-lifecycle'
import { mergeAnthropicUsage } from './llm-cache-diagnostics'
import { getDisallowedToolCallResult, shouldUsePlainTextFallback } from './structured-output'

type ContentBlock = Record<string, unknown> & { type: string }
interface ToolCall { id: string, name: string, input: Record<string, unknown> }

export interface AnthropicStreamResult {
  textContent: string
  contentBlocks: ContentBlock[]
  toolCalls: ToolCall[]
  stopReason: string
  usage?: Record<string, number>
}

function logAnthropicResponse(result: AnthropicStreamResult): void {
  if (!import.meta.env.DEV)
    return

  console.info('[Anthropic response]', {
    stopReason: result.stopReason,
    textContent: result.textContent,
    contentBlocks: result.contentBlocks.map(block => ({
      type: block.type,
      text: block.text,
      thinking: block.thinking,
      input: block.input,
      id: block.id,
      name: block.name,
    })),
    toolCalls: result.toolCalls,
    usage: result.usage,
  })
}

interface RawEvent {
  type: string
  index: number
  content_block?: ContentBlock
  delta?: { type?: string, text?: string, thinking?: string, signature?: string, partial_json?: string, stop_reason?: string }
  message?: { usage?: Record<string, number> }
  usage?: Record<string, number>
  error?: { type?: string, message?: string }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** 不将损坏或未结束的工具输入伪装成空对象。错误只包含结构信息，不包含聊天正文。 */
export async function readAnthropicStream(response: Response, onEvent: (event: StreamEvent) => void | Promise<void>): Promise<AnthropicStreamResult> {
  const requestId = response.headers.get('request-id') ?? response.headers.get('x-request-id')
  const fail = (reason: string): never => {
    throw new Error(`Anthropic stream: ${reason}${requestId ? ` (request ${requestId})` : ''}`)
  }
  const responseBody = response.body
  if (!responseBody)
    return fail('missing response body')

  const result: AnthropicStreamResult = { textContent: '', contentBlocks: [], toolCalls: [], stopReason: '' }
  const openBlocks = new Set<number>()
  const tools = new Map<number, { call: ToolCall, json: string }>()
  let stopped = false

  const consume = async (data: string): Promise<void> => {
    if (!data || data === '[DONE]')
      return
    let parsed: unknown
    try {
      parsed = JSON.parse(data)
    }
    catch {
      return fail(`invalid event JSON (${data.length} characters)`)
    }
    if (!isObject(parsed) || typeof parsed.type !== 'string')
      return fail('invalid event envelope')
    const event = parsed as unknown as RawEvent
    switch (event.type) {
      case 'error':
        return fail(`upstream ${event.error?.type ?? 'error'}: ${event.error?.message ?? 'unknown error'}`)
      case 'message_start':
        result.usage = mergeAnthropicUsage(result.usage, event.message?.usage)
        break
      case 'content_block_start': {
        const block = event.content_block
        if (!block || !Number.isInteger(event.index) || event.index < 0 || openBlocks.has(event.index))
          return fail('invalid content block start')
        result.contentBlocks[event.index] = { ...block! }
        openBlocks.add(event.index)
        if (block!.type === 'tool_use') {
          if (typeof block!.id !== 'string' || !block!.id || typeof block!.name !== 'string' || !block!.name)
            return fail('tool_use missing id/name')
          const call: ToolCall = {
            id: block!.id as string,
            name: block!.name as string,
            input: isObject(block!.input) ? block!.input as Record<string, unknown> : {},
          }
          tools.set(event.index, { call, json: '' })
          await onEvent({ type: 'tool-call-streaming-start', toolCallId: call.id, toolName: call.name })
        }
        else if (block!.type === 'text' && typeof block!.text === 'string' && block!.text) {
          result.textContent += block!.text
          await onEvent({ type: 'text-delta', text: block!.text })
        }
        break
      }
      case 'content_block_delta': {
        if (!openBlocks.has(event.index))
          return fail('delta for unopened content block')
        const block = result.contentBlocks[event.index]
        const delta = event.delta
        if (delta?.type === 'text_delta' && block.type === 'text' && typeof delta.text === 'string') {
          block.text = `${block.text ?? ''}${delta.text}`
          result.textContent += delta.text
          await onEvent({ type: 'text-delta', text: delta.text })
        }
        else if (delta?.type === 'thinking_delta' && block.type === 'thinking' && typeof delta.thinking === 'string') {
          block.thinking = `${block.thinking ?? ''}${delta.thinking}`
          await onEvent({ type: 'reasoning-delta', text: delta.thinking })
        }
        else if (delta?.type === 'signature_delta' && block.type === 'thinking' && typeof delta.signature === 'string') {
          block.signature = `${block.signature ?? ''}${delta.signature}`
        }
        else if (delta?.type === 'input_json_delta') {
          const tool = tools.get(event.index)
          if (!tool || typeof delta.partial_json !== 'string')
            return fail('invalid tool input delta')
          tool!.json += delta.partial_json
        }
        break
      }
      case 'content_block_stop': {
        if (!openBlocks.delete(event.index))
          return fail('stop for unopened content block')
        const tool = tools.get(event.index)
        if (tool) {
          let input: unknown = tool.call.input
          if (tool.json.trim()) {
            try {
              input = JSON.parse(tool.json)
            }
            catch {
              return fail(`invalid input JSON for ${tool.call.name} (${tool.json.length} characters)`)
            }
          }
          if (!isObject(input))
            return fail(`input for ${tool.call.name} must be an object`)
          tool.call.input = input as Record<string, unknown>
          result.contentBlocks[event.index].input = input
          result.toolCalls.push(tool.call)
          await onEvent({ type: 'tool-call', toolCallId: tool.call.id, toolName: tool.call.name, args: input } as StreamEvent)
          tools.delete(event.index)
        }
        break
      }
      case 'message_delta':
        result.usage = mergeAnthropicUsage(result.usage, event.usage)
        if (event.delta?.stop_reason)
          result.stopReason = event.delta.stop_reason
        break
      case 'message_stop':
        stopped = true
        break
    }
  }

  const reader = responseBody.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let dataLines: string[] = []
  const line = async (value: string): Promise<void> => {
    const normalized = value.replace(/\r$/, '')
    if (!normalized) {
      await consume(dataLines.join('\n'))
      dataLines = []
    }
    else if (normalized.startsWith('data:')) {
      dataLines.push(normalized.slice(5).replace(/^ /, ''))
    }
  }
  try {
    while (true) {
      const chunk = await reader.read()
      buffer += chunk.done ? decoder.decode() : decoder.decode(chunk.value, { stream: true })
      let end: number
      while (true) {
        end = buffer.indexOf('\n')
        if (end < 0)
          break
        await line(buffer.slice(0, end))
        buffer = buffer.slice(end + 1)
      }
      if (chunk.done)
        break
    }
    if (buffer)
      await line(buffer)
    await line('')
    if (!stopped || !result.stopReason || openBlocks.size > 0)
      fail(`incomplete response (stop=${result.stopReason || 'missing'}, openBlocks=${openBlocks.size})`)
    result.contentBlocks = result.contentBlocks.filter(Boolean)
    logAnthropicResponse(result)
    return result
  }
  finally {
    await reader.cancel().catch(() => {})
    reader.releaseLock()
  }
}

interface AnthropicLoopDeps {
  streamOnce: (messages: unknown[]) => Promise<AnthropicStreamResult>
  toolExecutors: Map<string, (input: unknown, opts: unknown) => Promise<unknown>>
  terminalToolNames: Set<string>
  lifecycleByName?: ReadonlyMap<string, ToolLifecycle>
  maxSteps: number
  onEvent: (event: StreamEvent) => void | Promise<void>
  onToolBudgetExceeded?: () => void
  requireToolCall?: boolean
  allowedToolNames?: ReadonlySet<string>
}

export async function runAnthropicToolLoop(initialMessages: unknown[], deps: AnthropicLoopDeps): Promise<void> {
  let messages = [...initialMessages]
  const ledger = new ToolExecutionLedger(deps.maxSteps)
  for (let step = 0; step < deps.maxSteps; step++) {
    const result = await deps.streamOnce(messages)
    if (deps.requireToolCall && result.textContent.trim()) {
      await deps.onEvent({ type: 'text-delta', text: result.textContent })
    }
    const hasToolCall = result.stopReason === 'tool_use' && result.toolCalls.length > 0
    if (!hasToolCall) {
      // 某些 Anthropic 兼容代理会忽略 tool_choice:any，但仍返回有效文本。
      // 让这段文本走现有 processAIResponse 防线，避免一次代理能力抖动变成无回复。
      await deps.onEvent({ type: 'finish', finishReason: result.stopReason === 'end_turn' ? 'stop' : result.stopReason })
      return
    }
    if (shouldUsePlainTextFallback(
      result.toolCalls.map(tool => tool.name),
      result.textContent,
      deps.requireToolCall,
      deps.allowedToolNames,
    )) {
      await deps.onEvent({ type: 'finish', finishReason: 'stop' })
      return
    }
    const content = deps.requireToolCall ? result.contentBlocks.filter(block => block.type !== 'text') : result.contentBlocks
    const results: Array<{ type: string, tool_use_id: string, content: string, is_error?: boolean }> = []
    for (const call of result.toolCalls) {
      if (deps.allowedToolNames && !deps.allowedToolNames.has(call.name)) {
        results.push({
          type: 'tool_result',
          tool_use_id: call.id,
          content: getDisallowedToolCallResult(call.name, deps.allowedToolNames),
          is_error: true,
        })
        continue
      }
      const lifecycle = getToolLifecycleByName(call.name, deps.lifecycleByName, deps.terminalToolNames)
      const claim = ledger.claim(call.id, call.name, call.input)
      let output: string
      let error: unknown
      if (!claim.accepted) {
        output = duplicateToolResult(claim)
        if (claim.reason === 'budget') {
          ledger.notifyBudgetOnce(deps.onToolBudgetExceeded)
          await deps.onEvent({ type: 'finish', finishReason: 'stop' })
          return
        }
      }
      else {
        const executor = deps.toolExecutors.get(call.name)
        if (!executor && lifecycle === 'terminal')
          throw new Error(`Terminal tool "${call.name}" is not registered`)
        try {
          output = executor ? normalizeToolResult(await executor(call.input, {}), call.name, lifecycle) : `Tool "${call.name}" not found`
        }
        catch (caught) {
          error = caught
          output = caught instanceof Error ? caught.message : String(caught)
        }
      }
      await deps.onEvent({ type: 'tool-result', toolCallId: call.id, result: output })
      if (claim.accepted && lifecycle === 'terminal') {
        if (!error) {
          for (const ignored of result.toolCalls.slice(result.toolCalls.indexOf(call) + 1))
            await deps.onEvent({ type: 'tool-result', toolCallId: ignored.id, result: ignoredAfterTerminalResult() })
          await deps.onEvent({ type: 'finish', finishReason: 'stop' })
          return
        }
        if (!ledger.allowInputCorrection(error, call.id, call.name, call.input)) {
          if (error instanceof ToolInputError) {
            console.warn(`[Anthropic] 工具参数连续无效，按静默结束本轮: ${call.name}`)
            await deps.onEvent({ type: 'finish', finishReason: 'stop' })
            return
          }
          throw error
        }
      }
      results.push({ type: 'tool_result', tool_use_id: call.id, content: output, ...(error ? { is_error: true } : {}) })
      if (ledger.budgetReached) {
        ledger.notifyBudgetOnce(deps.onToolBudgetExceeded)
        await deps.onEvent({ type: 'finish', finishReason: 'stop' })
        return
      }
    }
    messages = [...messages, { role: 'assistant', content }, { role: 'user', content: results }]
  }
  await deps.onEvent({ type: 'finish', finishReason: 'stop' })
}
